import React, { useState, useEffect, useCallback } from 'react';
import { Apple, ChevronLeft, ChevronRight } from 'lucide-react';
import { getPlayers, getNutritionDiary, saveNutritionGoals } from '../api';
import { useUrlParam } from '../hooks/useUrlParam';

// Own copy of the meal list (same convention as PlayerNutrition.js).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long' });
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});

// Heuristic "on track" band — within 90–110 % of goal reads as on target; outside is flagged.
// Deliberately simple for v1; adjust the band here if the staff want it tighter/looser.
function statusFor(value, goal) {
  if (!goal) return { pct: null, label: '—', color: 'var(--text-dimmed)' };
  const pct = Math.round((value / goal) * 100);
  if (pct >= 90 && pct <= 110) return { pct, label: 'on track', color: '#10b981' };
  return { pct, label: pct < 90 ? 'under goal' : 'over goal', color: '#f59e0b' };
}

function TotalRow({ label, value, goal, unit }) {
  const s = statusFor(value, goal);
  const width = s.pct == null ? 0 : Math.min(100, s.pct);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}
          {s.pct != null && <span style={{ color: s.color, fontWeight: 600 }}>{`  ·  ${s.pct}% (${s.label})`}</span>}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: s.color, borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

// ── Read-only day breakdown (per meal, per food) ────────────────────────────
function DayBreakdown({ entries }) {
  if (!entries.length) {
    return <div style={{ fontSize: 13, color: 'var(--text-dimmed)', padding: '8px 0' }}>Nothing logged for this day.</div>;
  }
  return (
    <div>
      {MEALS.map(m => {
        const rows = entries.filter(e => e.meal_type === m.id);
        if (!rows.length) return null;
        const kcal = rows.reduce((s, e) => s + e.kcal, 0);
        return (
          <div key={m.id} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              <span>{m.label}</span><span>{kcal} kcal</span>
            </div>
            {rows.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: 'var(--text-primary)' }}>
                  {e.food_name}
                  <span style={{ color: 'var(--text-dimmed)' }}>
                    {'  '}{e.amount} {e.unit === 'portion' ? `portion (${e.grams} g)` : 'g'}
                  </span>
                </span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {e.kcal} kcal · P {e.protein_g} / C {e.carbs_g} / F {e.fat_g}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Goals editor ───────────────────────────────────────────────────────────
const GOAL_FIELDS = [
  { key: 'kcal_goal',      label: 'Calories (kcal)' },
  { key: 'protein_g_goal', label: 'Protein (g)' },
  { key: 'carbs_g_goal',   label: 'Carbs (g)' },
  { key: 'fat_g_goal',     label: 'Fat (g)' },
];

function GoalsEditor({ playerId, goal, onSaved, user }) {
  const blank = { kcal_goal: '', protein_g_goal: '', carbs_g_goal: '', fat_g_goal: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    setForm(goal
      ? {
          kcal_goal: goal.kcal_goal, protein_g_goal: goal.protein_g_goal,
          carbs_g_goal: goal.carbs_g_goal, fat_g_goal: goal.fat_g_goal,
        }
      : blank);
    setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, goal]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    for (const f of GOAL_FIELDS) {
      if (form[f.key] === '' || !(Number(form[f.key]) >= 0)) {
        setMsg({ ok: false, text: `${f.label}: enter a number of 0 or higher` });
        return;
      }
    }
    setSaving(true); setMsg(null);
    try {
      await saveNutritionGoals({
        player_id: playerId,
        kcal_goal: Number(form.kcal_goal),
        protein_g_goal: Number(form.protein_g_goal),
        carbs_g_goal: Number(form.carbs_g_goal),
        fat_g_goal: Number(form.fat_g_goal),
        updated_by: user?.id,
      });
      setMsg({ ok: true, text: 'Goals saved.' });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Daily goals</div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Set by staff only. Applies every day until you change it.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {GOAL_FIELDS.map(f => (
          <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{f.label}</label>
            <input type="number" min="0" step="1" value={form[f.key]}
              onChange={e => set(f.key, e.target.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <button style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save goals'}</button>
        {msg && <span style={{ fontSize: 12, fontWeight: 600, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
      </div>
      {goal?.updated_at && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
          Last updated: {new Date(goal.updated_at).toLocaleDateString('en-GB')}
        </div>
      )}
    </div>
  );
}

export default function Nutrition({ user }) {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useUrlParam('player_id', '');
  const [date, setDate] = useUrlParam('date', tod());
  const [data, setData] = useState({ entries: [], totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }, goal: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPlayers()
      .then(p => {
        const active = (p || []).filter(pl => pl.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlayers(active);
        if (!playerId && active.length) setPlayerId(String(active[0].id));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!playerId || !date) return;
    setLoading(true);
    try { setData(await getNutritionDiary(playerId, date)); }
    catch { setData({ entries: [], totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }, goal: null }); }
    finally { setLoading(false); }
  }, [playerId, date]);

  useEffect(() => { load(); }, [load]);

  const { totals, goal } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Apple size={20} color="var(--text-primary)" />
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Nutrition</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px' }}>
        What a player logged each day in the Food Tracker, plus the daily goals you set for that player.
      </p>

      {/* Controls */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
          <option value="">— select player —</option>
          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, -1))}><ChevronLeft size={15} /></button>
          <input type="date" value={date} onChange={e => setDate(e.target.value || tod())} style={inputStyle} />
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, 1))}><ChevronRight size={15} /></button>
          <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
        </div>
      </div>

      {!playerId ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>Select a player.</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Left — day view */}
          <div style={{ flex: '2 1 420px', minWidth: 0 }}>
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Day overview</div>
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>{fmtDay(date)}</div>
              {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : <DayBreakdown entries={data.entries} />}
            </div>

            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total vs. goal</div>
              <TotalRow label="Calories" value={totals.kcal}      goal={goal?.kcal_goal}      unit=" kcal" />
              <TotalRow label="Protein"  value={totals.protein_g} goal={goal?.protein_g_goal} unit=" g" />
              <TotalRow label="Carbs"    value={totals.carbs_g}   goal={goal?.carbs_g_goal}   unit=" g" />
              <TotalRow label="Fat"      value={totals.fat_g}     goal={goal?.fat_g_goal}     unit=" g" />
              {!goal && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>No goals set for this player yet.</div>}
            </div>
          </div>

          {/* Right — goals editor */}
          <div style={{ flex: '1 1 300px', minWidth: 280 }}>
            <GoalsEditor playerId={playerId} goal={goal} onSaved={load} user={user} />
          </div>
        </div>
      )}
    </div>
  );
}
